import { createSelector } from '@reduxjs/toolkit'
import {
  isFobSelection,
  isFobSportsDetails,
  isFobSportsSelection,
} from '@mobi/betslip/helpers/typeGuards'
import { InvestmentType } from '@mobi/betslip/types'
import { isSelectionAllowedInMulti } from '@mobi/betslip/helpers/state'
import type { AppState } from '..'

export const selectBetSlipItems = (state: AppState) => state.betslip.bets.items
export const selectMultiInvestment = (state: AppState) => state.betslip.bets.multiInvestment
export const selectMultiBetError = (state: AppState) => state.betslip.bets.multiBetError
export const selectMultiBetReceipt = (state: AppState) => state.betslip.bets.multiReceipt

export const selectBetSlipItemCount = createSelector([selectBetSlipItems], items => {
  return items.length
})

export const selectBetSlipMultiLegs = createSelector([selectBetSlipItems], items => {
  return items.filter(item => isSelectionAllowedInMulti(item.selection))
})

export const selectBetSlipItemExternalBetIds = createSelector([selectBetSlipItems], items => {
  return items.flatMap(item =>
    isFobSportsSelection(item.selection) ? [item.selection.externalBetId] : []
  )
})

export const selectBetSlipItemReceipt = (itemId: string) =>
  createSelector([selectBetSlipItems], items => {
    const selectedItem = items.find(item => item.id === itemId)

    return selectedItem?.receipt ?? null
  })

export const selectBetSlipItemAppliedBonus = (
  itemId: string,
  investmentType: InvestmentType,
  totalBonus: number
) =>
  createSelector([selectBetSlipItems], items => {
    const selectedItem = items.find(item => item.id === itemId)

    if (!selectedItem) {
      return 0
    }

    const { win, place } = selectedItem.investment

    if (investmentType === 'Win') {
      return Math.max(Math.min(totalBonus, win.value), 0)
    }

    if (investmentType === 'Place') {
      return Math.max(Math.min(totalBonus - win.value, place.value), 0)
    }

    return 0
  })

export const selectBetSlipItemIsEachWay = (itemId: string) =>
  createSelector([selectBetSlipItems], items => {
    const selectedItem = items.find(item => item.id === itemId)

    return !!selectedItem?.isEachWay
  })

export const selectBetSlipItemIsEachWayAvailable = (itemId: string) =>
  createSelector([selectBetSlipItems], items => {
    const selectedItem = items.find(item => item.id === itemId)

    return !!selectedItem?.isEachWayAvailable
  })

export const selectBetSlipItemBonusBet = (itemId: string) =>
  createSelector([selectBetSlipItems], items => {
    const selectedItem = items.find(item => item.id === itemId)

    if (!selectedItem) {
      return 'Win'
    }

    return selectedItem.investment.place?.value > 0 ? 'Place' : 'Win'
  })

/** Primarily used for price change push subscriptions */
export const selectBetSlipFobSportsSelectionIds = createSelector([selectBetSlipItems], items =>
  items.flatMap(item => {
    if (!isFobSelection(item.selection) || !isFobSportsDetails(item.selectionDetails)) return []

    const { marketId, marketTypeCode, outcomeId } = item.selectionDetails
    return [{ marketId, marketTypeCode, outcomeId }]
  })
)

/** Primarily used for price change push subscriptions */
export const selectBetSlipFobRacingSelectionIds = createSelector([selectBetSlipItems], items =>
  items.flatMap(item => {
    if (!isFobSelection(item.selection) || isFobSportsDetails(item.selectionDetails)) return []

    return [item.selection.propositionSeq]
  })
)
